import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

import type { BankTxRow, ExpenseRow } from "@/components/finance/banking/reconciliation/types";
import {
  fetchInternalTransfers,
  matchInternalTransfers,
  type InternalTransferRow,
  type TransferKind,
} from "./matchInternalTransfersHelper";

export type SolvedPair = {
  expense_id: string;
  bank_transaction_id: string;
  score: 100 | 99;
  kind: "ref_exact" | "auto_exact" | "ref_exact_bulk";
  reasons: string[];
};

export type MatchedExpenseInfo = {
  type: "expense";
  expense_id: string;
  score: 100 | 99;
  kind: "ref_exact" | "auto_exact" | "ref_exact_bulk";
  reasons: string[];
  expense: ExpenseRow;
};

export type MatchedTransferInfo = {
  type: "transfer";
  transfer_id: string;
  score: 100;
  kind: TransferKind;
  reasons: string[];
  transfer: InternalTransferRow;
  side: "from" | "to";
};

export type MatchedBankRow = {
  tx: BankTxRow;
  match: MatchedExpenseInfo | MatchedTransferInfo;
};

type Params = {
  dateFrom: string;
  dateTo: string;
  bankAccountId?: string | null;

  daysWindow?: number; // default 3
  amountTolerance?: number; // default 1
  pctTolerance?: number; // default 0.02
};

const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const dayKey = (isoOrDate: string): string => {
  const d = new Date(isoOrDate);
  if (!Number.isFinite(d.getTime())) return isoOrDate.slice(0, 10);
  return d.toISOString().slice(0, 10);
};

const dayDiff = (aIsoOrDate: string, bIsoOrDate: string): number => {
  const a = new Date(aIsoOrDate);
  const b = new Date(bIsoOrDate);

  if (!Number.isFinite(a.getTime()) || !Number.isFinite(b.getTime())) {
    const aKey = aIsoOrDate.slice(0, 10);
    const bKey = bIsoOrDate.slice(0, 10);
    return Math.round((Date.parse(aKey) - Date.parse(bKey)) / 86400000);
  }

  return Math.round(
    (Date.parse(dayKey(aIsoOrDate)) - Date.parse(dayKey(bIsoOrDate))) / 86400000
  );
};

const extractRefTokens = (s?: string | null): string[] => {
  if (!s) return [];
  // Keep structured references such as TRANSFER-2238050164 intact and ignore
  // generic words like TRANSFER so same-day transfers don't cross-match.
  const up = s.toUpperCase().replace(/[|,;/]+/g, " ");
  const hits = up.match(/[A-Z0-9]+(?:-[A-Z0-9]+)+|[A-Z0-9]{6,32}/g) ?? [];
  return Array.from(new Set(hits.filter((tok) => /\d/.test(tok))));
};

type AmountBest = {
  bestLabel: "bank_amount" | "bank_amount_plus_fee" | "bank_amount_minus_fee";
  bestDelta: number;
};

const bestAmountDelta = (expVals: number[], amount: number, fee: number): AmountBest => {
  const variants = [
    { label: "bank_amount" as const, v: Math.abs(amount) },
    { label: "bank_amount_plus_fee" as const, v: Math.abs(amount) + fee },
    { label: "bank_amount_minus_fee" as const, v: Math.abs(amount) - fee },
  ];

  let best: AmountBest = { bestLabel: "bank_amount", bestDelta: Infinity };

  for (const ev of expVals) {
    for (const vv of variants) {
      const d = Math.abs(ev - vv.v);
      if (d < best.bestDelta) {
        best = { bestLabel: vv.label, bestDelta: d };
      }
    }
  }

  return best;
};

const isAmountPlausible = (
  expVals: number[],
  amount: number,
  fee: number,
  amountTolerance: number,
  pctTolerance: number
) => {
  const absTol = Math.max(amountTolerance * 3, 2);
  const bankAmount = Math.abs(amount);
  const variants = [bankAmount, bankAmount + fee, bankAmount - fee];

  return expVals.some((ev) =>
    variants.some((vv) => {
      const delta = Math.abs(ev - vv);
      const relTol = Math.max(absTol, Math.abs(ev) * pctTolerance);
      return delta <= relTol;
    })
  );
};

const normType = (v: unknown) => String(v ?? "").trim().toLowerCase();
const isDebitTx = (t: any) => normType(t.transaction_type) === "debit";

export function useMatchedBankTransactions(params: Params) {
  const {
    dateFrom,
    dateTo,
    bankAccountId,
    daysWindow = 3,
    amountTolerance = 1,
    pctTolerance = 0.02,
  } = params;

  return useQuery({
    queryKey: [
      "matched-bank-transactions-v1",
      {
        dateFrom,
        dateTo,
        bankAccountId,
        daysWindow,
        amountTolerance,
        pctTolerance,
      },
    ],
    queryFn: async () => {
      // --- expenses ---
      let expQ = supabase
        .from("expenses")
        .select("*")
        .gte("payment_date", dateFrom)
        .lte("payment_date", dateTo)
        .in("payment_status", ["paid", "Paid", "PAID"]);

      if (bankAccountId) expQ = expQ.eq("bank_account_id", bankAccountId);
      expQ = expQ.limit(10000);

      const { data: expensesRaw, error: expErr } = await expQ;
      if (expErr) throw expErr;

      const tsFrom = `${dateFrom}T00:00:00+03:00`;

      const toPlus1 = new Date(`${dateTo}T00:00:00+03:00`);
      toPlus1.setUTCDate(toPlus1.getUTCDate() + 1);
      const tsToPlus1 = toPlus1.toISOString();

      // strict range for bank tab UI
      let txStrictQ = supabase
        .from("bank_transactions")
        .select("*")
        .gte("transaction_date", tsFrom)
        .lt("transaction_date", tsToPlus1);

      if (bankAccountId) txStrictQ = txStrictQ.eq("bank_account_id", bankAccountId);
      txStrictQ = txStrictQ.limit(10000);

      const { data: txStrictRaw, error: txStrictErr } = await txStrictQ;
      if (txStrictErr) throw txStrictErr;

      // extended range for matching
      const fromExt = new Date(tsFrom);
      fromExt.setUTCDate(fromExt.getUTCDate() - daysWindow);

      const toExt = new Date(tsToPlus1);
      toExt.setUTCDate(toExt.getUTCDate() + daysWindow);

      let txExtQ = supabase
        .from("bank_transactions")
        .select("*")
        .gte("transaction_date", fromExt.toISOString())
        .lt("transaction_date", toExt.toISOString());

      if (bankAccountId) txExtQ = txExtQ.eq("bank_account_id", bankAccountId);
      txExtQ = txExtQ.limit(10000);

      const { data: txExtRaw, error: txExtErr } = await txExtQ;
      if (txExtErr) throw txExtErr;

      const expenses = (expensesRaw ?? []) as ExpenseRow[];
      const txsStrict = (txStrictRaw ?? []) as BankTxRow[];
      const txsExt = (txExtRaw ?? []) as BankTxRow[];

      const expN = expenses.map((e) => ({
        ...e,
        total_amount: num((e as any).total_amount ?? (e as any).expense_total),
        net_payment: num((e as any).net_payment),
      }));

      const txStrictN = txsStrict.map((t) => ({
        ...t,
        transaction_amount: num((t as any).transaction_amount),
        transaction_fee: num((t as any).transaction_fee),
      }));

      const txExtN = txsExt.map((t) => ({
        ...t,
        transaction_amount: num((t as any).transaction_amount),
        transaction_fee: num((t as any).transaction_fee),
      }));

      const txStrictDebit = txStrictN.filter(isDebitTx);
      const txExtDebit = txExtN.filter(isDebitTx);

      // strictTxIds includes ALL strict-range bank tx (debit + credit) because
      // internal transfers can match either side. Expense matching still uses
      // debit-only pools.
      const strictTxIds = new Set(txStrictN.map((t: any) => t.id));
      const paidDateOf = (e: ExpenseRow) => (e.payment_date ?? e.expense_date) as string;

      const solved: SolvedPair[] = [];
      const lockedExpenses = new Set<string>();
      const lockedTx = new Set<string>();

      const expenseById = new Map<string, ExpenseRow>();
      for (const e of expN) {
        expenseById.set((e as any).id, e);
      }

      const txById = new Map<string, BankTxRow>();
      for (const t of txStrictN) {
        txById.set((t as any).id, t);
      }
      for (const t of txExtDebit) {
        if (!txById.has((t as any).id)) txById.set((t as any).id, t);
      }

      const txByRefToken = new Map<string, BankTxRow[]>();

      for (const t of txExtDebit) {
        const tokens = extractRefTokens(t.transaction_id_reference);
        for (const tok of tokens) {
          const arr = txByRefToken.get(tok) ?? [];
          arr.push(t);
          txByRefToken.set(tok, arr);
        }
      }

      // Tier 0: bulk ref-exact (N expenses sharing one reference -> 1 bank debit)
      {
        const expByRefToken = new Map<string, ExpenseRow[]>();
        for (const e of expN) {
          const refValue =
            (e as any).payment_reference ?? (e as any).reference_number ?? null;
          const tokens = extractRefTokens(refValue);
          for (const tok of tokens) {
            const arr = expByRefToken.get(tok) ?? [];
            arr.push(e);
            expByRefToken.set(tok, arr);
          }
        }

        for (const [tok, group] of expByRefToken.entries()) {
          if (group.length < 2) continue;
          const txCands = (txByRefToken.get(tok) ?? []).filter(
            (t) => !lockedTx.has((t as any).id)
          );
          if (!txCands.length) continue;

          const accCount = new Map<string, number>();
          for (const e of group) {
            const acc = e.bank_account_id;
            if (!acc) continue;
            accCount.set(acc, (accCount.get(acc) ?? 0) + 1);
          }
          let dominantAcc: string | null = null;
          let maxN = 0;
          for (const [acc, n] of accCount.entries()) {
            if (n > maxN) {
              maxN = n;
              dominantAcc = acc;
            }
          }
          const groupForAcc = group.filter(
            (e) => e.bank_account_id === dominantAcc &&
              !lockedExpenses.has((e as any).id)
          );
          const txForAcc = txCands.filter((t) => t.bank_account_id === dominantAcc);
          if (groupForAcc.length < 2 || !txForAcc.length) continue;

          const sumTotal = groupForAcc.reduce(
            (acc, e) => acc + (num((e as any).total_amount) ?? 0),
            0
          );
          const sumNet = groupForAcc.reduce(
            (acc, e) =>
              acc + (num((e as any).net_payment) ?? num((e as any).total_amount) ?? 0),
            0
          );
          if (sumTotal <= 0 && sumNet <= 0) continue;

          const bulkAbsTol = Math.max(amountTolerance * 5, 5);
          let bestTx: BankTxRow | null = null;
          let bestDelta = Infinity;
          for (const t of txForAcc) {
            const a = num((t as any).transaction_amount);
            if (a == null) continue;
            const fee = num((t as any).transaction_fee) ?? 0;
            const variants = [Math.abs(a), Math.abs(a) + fee, Math.abs(a) - fee];
            for (const ev of [sumTotal, sumNet]) {
              if (ev <= 0) continue;
              const relTol = Math.max(bulkAbsTol, ev * pctTolerance);
              for (const v of variants) {
                const d = Math.abs(ev - v);
                if (d <= relTol && d < bestDelta) {
                  bestDelta = d;
                  bestTx = t;
                }
              }
            }
          }
          if (!bestTx) continue;

          const tId = (bestTx as any).id as string;
          for (const e of groupForAcc) {
            const eId = (e as any).id as string;
            if (lockedExpenses.has(eId)) continue;
            solved.push({
              expense_id: eId,
              bank_transaction_id: tId,
              score: 100,
              kind: "ref_exact_bulk",
              reasons: [
                `bulk_ref_match group_size=${groupForAcc.length} delta=${bestDelta.toFixed(2)}`,
              ],
            });
            lockedExpenses.add(eId);
          }
          lockedTx.add(tId);
        }
      }

      // Tier 1: reference exact
      for (const e of expN) {
        const eId = (e as any).id as string;
        if (lockedExpenses.has(eId)) continue;

        const eTokens = extractRefTokens(e.payment_reference);
        if (!eTokens.length) continue;

        const seen = new Set<string>();
        const candidates: BankTxRow[] = [];

        for (const tok of eTokens) {
          const list = txByRefToken.get(tok);
          if (!list?.length) continue;

          for (const t of list) {
            const tId = (t as any).id as string;
            if (seen.has(tId)) continue;
            seen.add(tId);
            candidates.push(t);
          }
        }

        const pool = candidates
          .filter((t) => !lockedTx.has((t as any).id))
          .filter((t) => (!e.bank_account_id ? true : e.bank_account_id === t.bank_account_id));

        if (!pool.length) continue;

        pool.sort(
          (a, b) =>
            Math.abs(dayDiff(a.transaction_date, paidDateOf(e))) -
            Math.abs(dayDiff(b.transaction_date, paidDateOf(e)))
        );

        const t = pool[0];

        solved.push({
          expense_id: eId,
          bank_transaction_id: (t as any).id,
          score: 100,
          kind: "ref_exact",
          reasons: ["Reference id match"],
        });

        lockedExpenses.add(eId);
        lockedTx.add((t as any).id);
      }

      // Tier 2: auto exact
      type Pair = { expense: ExpenseRow; tx: BankTxRow; reasons: string[] };
      const autoPairs: Pair[] = [];

      for (const e of expN) {
        const eId = (e as any).id as string;
        if (lockedExpenses.has(eId)) continue;

        const paidDate = paidDateOf(e);
        const expVals = [num((e as any).net_payment), num((e as any).total_amount)].filter(
          (x): x is number => x != null
        );
        if (!expVals.length) continue;

        for (const t of txExtDebit) {
          const tId = (t as any).id as string;
          if (lockedTx.has(tId)) continue;
          if (e.bank_account_id && e.bank_account_id !== t.bank_account_id) continue;

          const dd = Math.abs(dayDiff(t.transaction_date, paidDate));
          if (dd > 1) continue;

          const a = num((t as any).transaction_amount);
          if (a == null) continue;
          const fee = num((t as any).transaction_fee) ?? 0;

          if (!isAmountPlausible(expVals, a, fee, amountTolerance, pctTolerance)) continue;

          const amtBest = bestAmountDelta(expVals, a, fee);

          autoPairs.push({
            expense: e,
            tx: t,
            reasons: [
              "auto_exact",
              `date_diff_days=${dd}`,
              `amount_best=${amtBest.bestLabel}`,
              `amount_delta=${amtBest.bestDelta.toFixed(2)}`,
            ],
          });
        }
      }

      autoPairs.sort((p1, p2) => {
        const e1 = p1.expense as any;
        const e2 = p2.expense as any;
        const t1 = p1.tx as any;
        const t2 = p2.tx as any;

        const paid1 = paidDateOf(e1);
        const paid2 = paidDateOf(e2);

        const expVals1 = [num(e1.net_payment), num(e1.total_amount)].filter(
          (x): x is number => x != null
        );
        const expVals2 = [num(e2.net_payment), num(e2.total_amount)].filter(
          (x): x is number => x != null
        );

        const d1 = bestAmountDelta(
          expVals1,
          num(t1.transaction_amount) ?? 0,
          num(t1.transaction_fee) ?? 0
        ).bestDelta;

        const d2 = bestAmountDelta(
          expVals2,
          num(t2.transaction_amount) ?? 0,
          num(t2.transaction_fee) ?? 0
        ).bestDelta;

        if (d1 !== d2) return d1 - d2;

        const dd1 = Math.abs(dayDiff(t1.transaction_date, paid1));
        const dd2 = Math.abs(dayDiff(t2.transaction_date, paid2));
        return dd1 - dd2;
      });

      for (const p of autoPairs) {
        const eId = (p.expense as any).id as string;
        const tId = (p.tx as any).id as string;

        if (lockedExpenses.has(eId) || lockedTx.has(tId)) continue;

        solved.push({
          expense_id: eId,
          bank_transaction_id: tId,
          score: 99,
          kind: "auto_exact",
          reasons: p.reasons,
        });

        lockedExpenses.add(eId);
        lockedTx.add(tId);
      }

      const matchedBank: MatchedBankRow[] = [];

      for (const p of solved) {
        if (!strictTxIds.has(p.bank_transaction_id)) continue;

        const expense = expenseById.get(p.expense_id);
        const tx = txById.get(p.bank_transaction_id);
        if (!expense || !tx) continue;

        matchedBank.push({
          tx,
          match: {
            type: "expense",
            expense_id: p.expense_id,
            score: p.score,
            kind: p.kind,
            reasons: p.reasons,
            expense,
          },
        });
      }

      // Tier 3: internal transfer matches (added on top of expense matches).
      try {
        const transfers = await fetchInternalTransfers({
          dateFrom,
          dateTo,
          daysWindow,
          bankAccountId: bankAccountId ?? null,
        });

        const transferPairs = matchInternalTransfers({
          transfers,
          bankTxs: txStrictN as any,
          daysWindow,
          amountTolerance,
          pctTolerance,
          lockedTx,
        });

        for (const p of transferPairs) {
          if (!strictTxIds.has(p.bank_transaction_id)) continue;
          const tx = txById.get(p.bank_transaction_id);
          if (!tx) continue;

          matchedBank.push({
            tx,
            match: {
              type: "transfer",
              transfer_id: p.transfer_id,
              score: p.score,
              kind: p.kind,
              reasons: p.reasons,
              transfer: p.transfer,
              side: p.side,
            },
          });
          lockedTx.add(p.bank_transaction_id);
        }
      } catch (err) {
        console.warn("internal_transfers matched lookup failed:", err);
      }

      matchedBank.sort((a, b) => (b.match.score ?? 0) - (a.match.score ?? 0));

      return {
        solved,
        matchedBank,
      };
    },
    staleTime: 30_000,
  });
}